"use client";

import { useEffect, useState } from "react";

type Options = {
  /** Counting starts on the first render where this is true. */
  armed: boolean;
  duration?: number;
  /** Held after arming, so a row can tick in after the one above it. */
  delay?: number;
};

/**
 * Runs a number from 0 up to `target` once, on the same ease-out the section's
 * entrance uses so the figures settle in step with the card they sit in.
 *
 * Reduced motion gets the final number on the first frame instead — a counter
 * is decoration on a value that has to be readable either way.
 */
export function useCountUp(
  target: number,
  { armed, duration = 1400, delay = 0 }: Options,
) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!armed) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let raf = 0;
    let start: number | undefined;

    const tick = (now: number) => {
      // Handled here rather than in the effect body so the jump-to-final case
      // isn't a setState cascading straight out of the effect.
      if (reduced) {
        setValue(target);
        return;
      }

      start ??= now;
      const elapsed = now - start - delay;

      if (elapsed < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }

      const t = Math.min(1, elapsed / duration);
      // ease-out cubic, matching the card entrance's power3.out.
      setValue(target * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [armed, target, duration, delay]);

  return value;
}
